import { Group, Text, UnstyledButton } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { CSSProperties, Fragment, ReactElement, ReactNode, useState } from 'react';
import { ORBIT_DISABLED, ORBIT_GRAY, STATUS_BADGE_TONES } from '@/theme/colors';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { formatRateDate, formatRateShortDate, RateGroup, rateSeries, RateVersion, scheduledFrom } from './rateSchedule';

/** The prototype's rate schedule table (BAR-3039), shared by the fee and cost schedules. */

const TABLE_STYLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontVariantNumeric: 'tabular-nums',
  background: '#fff',
};
const CELL_STYLE: CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  lineHeight: '18px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
};
const HEAD_STYLE: CSSProperties = { ...CELL_STYLE, fontSize: 12, fontWeight: 500, color: ORBIT_GRAY };
const GROUP_STYLE: CSSProperties = { ...CELL_STYLE, background: 'rgba(0, 0, 0, 0.02)', paddingTop: 12 };
const BADGE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  padding: '1px 9px',
  borderRadius: 100,
  whiteSpace: 'nowrap',
};
const BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '5px 10px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
};
const GHOST_STYLE: CSSProperties = { ...BUTTON_STYLE, border: '1px solid transparent', background: 'transparent' };

function Badge({ tone, children, title }: { tone: 'neutral' | 'info'; children: ReactNode; title?: string }) {
  return (
    <span title={title} style={{ ...BADGE_STYLE, ...STATUS_BADGE_TONES[tone] }}>
      {children}
    </span>
  );
}

/** Base covers, Increment and Every; a dash where the rate has none (Unlimited, the daily minimum). */
function VariableCells({ version }: { version: RateVersion }): ReactElement {
  const variable = version.variable;
  const dash = <td style={{ ...CELL_STYLE, color: ORBIT_DISABLED }}>—</td>;
  if (!variable)
    return (
      <>
        {[dash, dash, dash].map((cell, index) => (
          <Fragment key={index}>{cell}</Fragment>
        ))}
      </>
    );
  const timed = variable.baseCoversMinutes !== null;
  return (
    <>
      <td style={CELL_STYLE}>{timed ? minutes(variable.baseCoversMinutes) : 'Unlimited'}</td>
      {timed && variable.increment !== null ? (
        <td style={CELL_STYLE}>{formatLedgerMoney(variable.increment)}</td>
      ) : (
        dash
      )}
      {timed ? <td style={CELL_STYLE}>{minutes(variable.incrementEveryMinutes)}</td> : dash}
    </>
  );
}

export interface RateScheduleTableProps {
  /** The cost schedule's partner rates (BAR-3047): Base covers, Increment and Every columns. */
  variableFees?: boolean;
  /** `Rate` by default; `Base fee`, `Fee`, `Commission per win`. */
  rateLabel?: string;
  groups: RateGroup[];
  /** `YYYY-MM-DD`. */
  today: string;
  procedureLabel: (procedure: string) => string;
  /** `Priya R.` for a `Practitioner/<id>`. */
  whoName: (reference: string) => string;
  onUpdate: (groupKey: string) => void;
  onCorrect: (groupKey: string) => void;
}

const minutes = (value: number | null | undefined): string => (value ? `${value} min` : '');

export function RateScheduleTable({
  variableFees = false,
  rateLabel = 'Rate',
  groups,
  today,
  procedureLabel,
  whoName,
  onUpdate,
  onCorrect,
}: RateScheduleTableProps): ReactElement {
  const columns = variableFees ? 9 : 6;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const corrected = (version: RateVersion): ReactNode =>
    version.correctedAt ? (
      <Badge
        tone="neutral"
        title={`Corrected ${formatRateDate(version.correctedAt.slice(0, 10))} by ${whoName(version.correctedBy ?? '')}: ${
          version.correctionReason ?? ''
        }. Was ${version.previousRate === null ? '—' : formatLedgerMoney(version.previousRate)}`}
      >
        Corrected
      </Badge>
    ) : null;

  const row = (
    key: string,
    label: string,
    version: RateVersion,
    until: string | null,
    actions: ReactNode,
    muted = false
  ): ReactElement => (
    <tr key={key} style={muted ? { color: ORBIT_DISABLED } : undefined}>
      <td style={CELL_STYLE}>{label}</td>
      <td style={CELL_STYLE}>{formatLedgerMoney(version.rate)}</td>
      {variableFees && <VariableCells version={version} />}
      <td style={CELL_STYLE}>{formatRateDate(version.effectiveFrom)}</td>
      <td style={{ ...CELL_STYLE, color: until ? undefined : ORBIT_DISABLED }}>
        {until ? formatRateDate(until) : '—'}
      </td>
      <td style={CELL_STYLE}>{whoName(version.setBy)}</td>
      <td style={CELL_STYLE}>
        <Group gap={6} wrap="nowrap">
          {actions}
        </Group>
      </td>
    </tr>
  );

  return (
    <table style={TABLE_STYLE}>
      <thead>
        <tr>
          <th style={HEAD_STYLE}>Procedure</th>
          <th style={HEAD_STYLE}>{rateLabel}</th>
          {variableFees && (
            <>
              <th style={HEAD_STYLE}>Base covers</th>
              <th style={HEAD_STYLE}>Increment</th>
              <th style={HEAD_STYLE}>Every</th>
            </>
          )}
          <th style={HEAD_STYLE}>Effective</th>
          <th style={HEAD_STYLE}>Until</th>
          <th style={HEAD_STYLE}>Set by</th>
          <th style={HEAD_STYLE} />
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          // A $0 daily minimum is none (BAR-3047): its row goes, history and all.
          const series = rateSeries(group.versions, today).filter(
            (entry) => ((entry.scheduled ?? entry.current)?.rate ?? 0) > 0 || (entry.current?.rate ?? 0) > 0
          );
          const pendingFrom = scheduledFrom(series);
          return (
            <Fragment key={group.key}>
              <tr>
                <td style={GROUP_STYLE} colSpan={columns}>
                  <Group gap={8} wrap="nowrap">
                    <Text fz={13} fw={600}>
                      {group.name}
                    </Text>
                    {pendingFrom && <Badge tone="info">{`Update from ${formatRateShortDate(pendingFrom)}`}</Badge>}
                    <Group gap={6} ml="auto" wrap="nowrap">
                      <UnstyledButton style={GHOST_STYLE} onClick={() => onCorrect(group.key)}>
                        Correct
                      </UnstyledButton>
                      <UnstyledButton style={BUTTON_STYLE} onClick={() => onUpdate(group.key)}>
                        Update rates
                      </UnstyledButton>
                    </Group>
                  </Group>
                </td>
              </tr>
              {series.length === 0 && (
                <tr>
                  <td style={{ ...CELL_STYLE, color: ORBIT_DISABLED }} colSpan={columns}>
                    No rates
                  </td>
                </tr>
              )}
              {series.map((entry) => {
                const shown = (entry.current ?? entry.scheduled) as RateVersion;
                const historyKey = `${group.key}|${entry.procedure}`;
                const label = procedureLabel(entry.procedure);
                return (
                  <Fragment key={historyKey}>
                    {row(
                      shown.id,
                      label,
                      shown,
                      entry.current && entry.scheduled ? entry.current.effectiveUntil : null,
                      <>
                        {corrected(shown)}
                        {entry.current ? (
                          entry.earlier.length > 0 && (
                            <UnstyledButton
                              fz={12}
                              c={ORBIT_GRAY}
                              onClick={() => toggle(historyKey)}
                              aria-expanded={open.has(historyKey)}
                            >
                              {`${open.has(historyKey) ? '▾' : '▸'} ${entry.earlier.length} earlier`}
                            </UnstyledButton>
                          )
                        ) : (
                          <Badge tone="info">Scheduled</Badge>
                        )}
                      </>
                    )}
                    {entry.current &&
                      entry.scheduled &&
                      row(entry.scheduled.id, '', entry.scheduled, null, <Badge tone="info">Scheduled</Badge>)}
                    {open.has(historyKey) &&
                      entry.earlier.map((version) =>
                        row(
                          version.id,
                          '',
                          version,
                          version.effectiveUntil,
                          <>
                            {corrected(version)}
                            <IconLock size={13} stroke={1.8} color={ORBIT_DISABLED} aria-label="Locked" />
                          </>,
                          true
                        )
                      )}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
